import React, { useEffect, useState, useContext } from "react";
import FaSummarySchedule from "../../../components/Schedules/CA/FaSummarySchedule";
import { AuthContext } from "../../../contexts/AuthContext";
import { useErrorDialog } from "../../../contexts/ErrorDialogContext";
import { useSuccessDialog } from "../../../contexts/SuccessDialogContext";
import ComponentLoading from "../../../components/App/ComponentLoading";
import { handleScheduleDeletion } from "../utils/handleScheduleDeletion";
import { useAccessControl } from "../../../utils/hooks/useAccessControl";
import { CA_SCHEDULES } from "../../../utils/data/controlData";

const alignHistoryAmount = (amount, history) => {
  const safeAmount = amount ?? 0;

  if (!Array.isArray(history) || history.length === 0) {
    return [safeAmount];
  }

  let sum = 0;
  for (let i = 0; i < history.length; i++) {
    sum += history[i] || 0;
  }

  // If mismatch, discard history and regenerate
  if (sum !== safeAmount) {
    return [safeAmount];
  }

  return history;
};

const getImportedAssetId = (rowOrCategory) => {
  if (rowOrCategory && typeof rowOrCategory === "object") {
    if (rowOrCategory.assetId) return rowOrCategory.assetId;
    if (rowOrCategory.categoryId) return rowOrCategory.categoryId;
    if (rowOrCategory.assetCategoryId) return rowOrCategory.assetCategoryId;
    rowOrCategory = rowOrCategory.assetCategory;
  }

  return `fa-register-${String(rowOrCategory || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")}`;
};

const buildHistoryAmount = (amount) => [amount ?? 0];

const isFaRegisterManualValuesRow = (row = {}) =>
  row.faRegisterManualValues || row.faRegisterValueMode === "manual";

const buildImportedFaSummaryRow = (importedRow, existingRow = {}) => {
  const useManualValues = isFaRegisterManualValuesRow(existingRow);
  const existingCost = existingRow.cost || {};
  const sourceOpeningBalance = importedRow.openingBalance || 0;
  const sourceAdditions = importedRow.additions || 0;
  const sourceDisposal = importedRow.disposal || 0;
  const openingBalance = useManualValues
    ? existingCost.openingBalance || 0
    : sourceOpeningBalance;
  const additions = useManualValues
    ? existingCost.additions || 0
    : sourceAdditions;
  const disposal = useManualValues ? existingCost.disposal || 0 : sourceDisposal;
  const assetCategory = importedRow.assetCategory || existingRow.assetCategory;
  const importedAssetId = getImportedAssetId(importedRow);

  return {
    ...existingRow,
    assetId: importedAssetId,
    assetCategory,
    isImportedFromFARegister: true,
    faRegisterSource: true,
    faRegisterManualValues: useManualValues,
    faRegisterValueMode: useManualValues ? "manual" : "source",
    faRegisterSourceSnapshot: {
      assetId: importedAssetId,
      assetCategory,
      openingBalance: sourceOpeningBalance,
      additions: sourceAdditions,
      disposal: sourceDisposal,
    },
    cost: {
      openingBalance,
      openingBalanceHistoryAmount: useManualValues
        ? alignHistoryAmount(
            openingBalance,
            existingCost.openingBalanceHistoryAmount
          )
        : buildHistoryAmount(openingBalance),
      additions,
      additionsHistoryAmount: useManualValues
        ? alignHistoryAmount(additions, existingCost.additionsHistoryAmount)
        : buildHistoryAmount(additions),
      disposal,
      disposalHistoryAmount: useManualValues
        ? alignHistoryAmount(disposal, existingCost.disposalHistoryAmount)
        : buildHistoryAmount(disposal),
    },
    accumulatedDepreciation: existingRow.accumulatedDepreciation || {
      openingBalance: 0,
      openingBalanceHistoryAmount: [0],
      additions: 0,
      additionsHistoryAmount: [0],
      disposal: 0,
      disposalHistoryAmount: [0],
    },
  };
};

const mergeFaRegisterSummaryRows = (dataRows, faRegisterImport) => {
  const faSummaryRows =
    faRegisterImport?.faSummary || faRegisterImport?.summaries?.faSummary || [];

  if (!faSummaryRows.length) return dataRows;

  const importedAssetIds = new Set(
    faSummaryRows.map((row) => getImportedAssetId(row))
  );
  const existingImportedById = dataRows.reduce((map, row) => {
    if (!row.isImportedFromFARegister && !row.faRegisterSource) return map;
    if (row.assetId) map[row.assetId] = row;
    return map;
  }, {});

  const importedRows = faSummaryRows
    .filter((row) => String(row.assetCategory || "").trim())
    .map((row) => {
      const importedAssetId = getImportedAssetId(row);
      return buildImportedFaSummaryRow(
        row,
        existingImportedById[importedAssetId]
      );
    });

  const manualRows = dataRows.filter(
    (row) => !row.isImportedFromFARegister && !row.faRegisterSource
  );
  const retainedManualImportedRows = dataRows.filter(
    (row) =>
      isFaRegisterManualValuesRow(row) &&
      (row.isImportedFromFARegister || row.faRegisterSource) &&
      row.assetId &&
      !importedAssetIds.has(row.assetId)
  );

  return [...manualRows, ...retainedManualImportedRows, ...importedRows];
};

const FaSummarySchedulePage = ({ scheduleId, company }) => {
  const { getCAScheduleDataByType, updateCASchedule, deleteCAScheduleByType } =
    useContext(AuthContext);
  const [saveTrigger, setSaveTrigger] = useState(false);
  const [schedule, setSchedule] = useState({});
  const [scheduleDetails, setScheduleDetails] = useState({});

  const { canEdit: canEditCASchedule } = useAccessControl(CA_SCHEDULES);

  const [loading, setLoading] = useState(true);

  const handleError = useErrorDialog();
  const handleSuccess = useSuccessDialog();

  const saveSchedule = async (schedule, scheduleDetails) => {
    if (!company || !scheduleId) {
      handleError("Missing UEN or Schedule ID. Please try again.");
      return false;
    }

    const updatedSchedule = {
      sch_id: scheduleId,
      scheduleType: "FASummary",
      costRows: scheduleDetails.costRows,
      accDepreciationRows: scheduleDetails.accDepreciationRows,
      totalCostAdditions: scheduleDetails.totalCostAdditions,
      totalCostDisposal: scheduleDetails.totalCostDisposal,
      doc: scheduleDetails.doc,
      notes: schedule.notes,
      reviewNotes: schedule.reviewNotes,
    };

    setLoading(true);
    try {
      const response = await updateCASchedule(updatedSchedule);
      if (!response) {
        throw new Error("The schedule could not be saved.");
      }

      handleSuccess("The schedule has been saved successfully.");
      setSaveTrigger((prev) => !prev);
      return true;
    } catch (error) {
      handleError(
        error,
        "There was a problem saving the schedule. Please contact support if the issue persists."
      );
      return false;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      try {
        // Fetch schedule data from API
        const response = await getCAScheduleDataByType(scheduleId, "FASummary");
        const scheduleData = response?.schedule;

        if (scheduleData) {
          const { costRows = [], accDepreciationRows = [] } = scheduleData;

          const accDepMap = accDepreciationRows.reduce((map, row) => {
            if (row.assetId) {
              map[row.assetId] = row;
            }
            return map;
          }, {});

          const dataRows = costRows.map((costItem) => {
            const accRow = accDepMap[costItem.assetId] || {};

            const costOpening = costItem.openingBalance || 0;
            const costAdditions = costItem.additions || 0;
            const costDisposal = costItem.disposal || 0;

            const accOpening = accRow.openingBalance || 0;
            const accAdditions = accRow.additions || 0;
            const accDisposal = accRow.disposal || 0;

            return {
              assetId: costItem.assetId || "Unknown",
              assetCategory: costItem.assetCategory || "Unknown",
              isImportedFromFARegister:
                costItem.isImportedFromFARegister ||
                accRow.isImportedFromFARegister ||
                false,
              faRegisterSource:
                costItem.faRegisterSource || accRow.faRegisterSource || false,
              faRegisterManualValues:
                costItem.faRegisterManualValues ||
                accRow.faRegisterManualValues ||
                costItem.faRegisterValueMode === "manual" ||
                accRow.faRegisterValueMode === "manual" ||
                false,
              faRegisterValueMode:
                costItem.faRegisterValueMode || accRow.faRegisterValueMode,
              faRegisterSourceSnapshot:
                costItem.faRegisterSourceSnapshot ||
                accRow.faRegisterSourceSnapshot ||
                null,
              cost: {
                openingBalance: costOpening,
                openingBalanceHistoryAmount: alignHistoryAmount(
                  costOpening,
                  costItem.openingBalanceHistoryAmount
                ),
                additions: costAdditions,
                additionsHistoryAmount: alignHistoryAmount(
                  costAdditions,
                  costItem.additionsHistoryAmount
                ),
                disposal: costItem.disposal || 0,
                disposalHistoryAmount: alignHistoryAmount(
                  costDisposal,
                  costItem.disposalHistoryAmount
                ),
              },
              accumulatedDepreciation: {
                openingBalance: accOpening,
                openingBalanceHistoryAmount: alignHistoryAmount(
                  accOpening,
                  accRow.openingBalanceHistoryAmount
                ),
                additions: accAdditions,
                additionsHistoryAmount: alignHistoryAmount(
                  accAdditions,
                  accRow.additionsHistoryAmount
                ),
                disposalHistoryAmount: alignHistoryAmount(
                  accDisposal,
                  accRow.disposalHistoryAmount
                ),
                disposal: accRow.disposal || 0,
              },
            };
          });
          const mergedDataRows = mergeFaRegisterSummaryRows(
            dataRows,
            scheduleData.faRegisterImport
          );

          setSchedule({
            type: "Fixed Asset Summary",
            scheduleNo: scheduleData.scheduleNo,
            name: "Fixed Asset Summary",
            notes: scheduleData.notes || "",
            reviewNotes: scheduleData.reviewNotes || "",
          });

          setScheduleDetails({
            dataRows: mergedDataRows,
            totalCostAdditions: scheduleData.totalCostAdditions || 0,
            totalCostDisposal: scheduleData.totalCostDisposal || 0,
          });
        }
      } catch (error) {
        handleError(error);
      } finally {
        setLoading(false);
      }
    };

    if (company && scheduleId) {
      fetchData();
    }
  }, [saveTrigger, scheduleId, company.uen]);

  const deleteSchedule = async () => {
    await handleScheduleDeletion({
      scheduleId,
      deleteFn: deleteCAScheduleByType,
      handleSuccess,
      handleError,
      setLoading,
    });
  };

  return (
    <>
      {loading ? (
        <ComponentLoading />
      ) : (
        <FaSummarySchedule
          company={company}
          schedule={schedule}
          scheduleDetails={scheduleDetails}
          saveSchedule={saveSchedule}
          deleteSchedule={deleteSchedule}
          canEditSchedule={canEditCASchedule}
        />
      )}
    </>
  );
};

export default FaSummarySchedulePage;
